import { useEffect, useMemo, useState } from "react";
import { getEmailPreviewDisplayAPI } from "../../auth/apiEmailMarketing";
import { isAuthenticated } from "../../auth";

export const PREVIEW_DISPLAY_EVENT = "elakay:email-preview-display";
let previewDisplayRequest;

export const PREVIEW_MODES = ["auto", "min", "max", "fixed", "responsive"];
export const PREVIEW_UNITS = ["px", "%", "vh", "vw"];
export const PREVIEW_OVERFLOWS = ["auto", "hidden", "scroll", "visible"];

export const DEFAULT_PREVIEW_DISPLAY = Object.freeze({
  global: {
    width: { mode: "responsive", value: null, unit: "px" },
    height: { mode: "auto", value: null, unit: "px" },
    minWidth: null,
    maxWidth: { value: 100, unit: "%" },
    minHeight: null,
    maxHeight: { value: 90, unit: "vh" },
    overflowX: "hidden",
    overflowY: "auto",
    responsive: { desktop: {}, laptop: {}, tablet: {}, mobile: {} },
  },
  modal: { inheritGlobal: true }, iframe: { inheritGlobal: true },
  productPreview: { inheritGlobal: true }, template: { inheritGlobal: true },
});

const cssLength = (length) => length && Number.isFinite(Number(length.value))
  ? `${Number(length.value)}${PREVIEW_UNITS.includes(length.unit) ? length.unit : "px"}`
  : undefined;

const deviceForWidth = (width) => width <= 576 ? "mobile" : width <= 768 ? "tablet" : width <= 1200 ? "laptop" : "desktop";

const mergeRule = (base, override) => ({ ...base, ...override, width: { ...base.width, ...override?.width }, height: { ...base.height, ...override?.height } });

export const resolvePreviewSize = (settings, previewType = "iframe", viewport = {}) => {
  const config = settings || DEFAULT_PREVIEW_DISPLAY;
  const global = mergeRule(DEFAULT_PREVIEW_DISPLAY.global, config.global || {});
  const override = config[previewType];
  let rule = override?.inheritGlobal === false ? mergeRule(global, override) : global;
  const viewportWidth = Number(viewport.width) || (typeof window !== "undefined" ? window.innerWidth : 1280);
  if (rule.width.mode === "responsive" || rule.height.mode === "responsive") {
    rule = mergeRule(rule, rule.responsive?.[deviceForWidth(viewportWidth)] || {});
  }
  const dimensionStyle = (dimension, axis) => {
    const length = cssLength(dimension);
    if (dimension?.mode === "fixed") return { [axis]: length };
    if (dimension?.mode === "min") return { [`min${axis[0].toUpperCase()}${axis.slice(1)}`]: length };
    if (dimension?.mode === "max") return { [`max${axis[0].toUpperCase()}${axis.slice(1)}`]: length };
    return axis === "width" ? { width: "100%" } : {};
  };
  const modeStyle = { ...dimensionStyle(rule.width, "width"), ...dimensionStyle(rule.height, "height") };
  // The standalone "Minimum/Maximum width/height" fields are meant to ADD
  // an extra, independent bound on top of whatever the Width/Height mode
  // picker already produced (e.g. mode=auto plus a safety max-width) --
  // they must never blindly replace a value the mode itself already set.
  // A plain `minWidth: cssLength(rule.minWidth)` declared after the
  // `...modeStyle` spread used to do exactly that: leaving the standalone
  // field empty (the common case -- most admins only touch the mode's own
  // value) reset it to `undefined`, discarding a Width/Height mode of
  // "Min"/"Max" the instant this object was built.
  return {
    ...modeStyle,
    minWidth: cssLength(rule.minWidth) ?? modeStyle.minWidth,
    maxWidth: cssLength(rule.maxWidth) ?? modeStyle.maxWidth ?? "100%",
    minHeight: cssLength(rule.minHeight) ?? modeStyle.minHeight,
    maxHeight: cssLength(rule.maxHeight) ?? modeStyle.maxHeight,
    overflowX: rule.overflowX || "hidden", overflowY: rule.overflowY || "auto",
  };
};

// Called by EmailMarketingSettings.jsx right after a successful
// previewDisplay save, alongside dispatching PREVIEW_DISPLAY_EVENT. The
// event alone only reaches components already mounted at save time --
// without also replacing this module-level cache, any preview that mounts
// *after* the save (e.g. navigating from Settings into the Editor without a
// full page reload) would still resolve `previewDisplayRequest` to the
// value fetched at the start of the session and silently show stale
// dimensions until the next hard refresh (spec: Settings/Editor
// synchronization, "do not create ad hoc caches" without invalidation).
export const setCachedPreviewDisplay = (previewDisplay) => {
  previewDisplayRequest = Promise.resolve({ previewDisplay });
};

export const useEmailPreviewDisplay = (candidateSettings) => {
  const [saved, setSaved] = useState(null);
  const { token } = isAuthenticated() || {};
  useEffect(() => {
    if (candidateSettings) return undefined;
    const receiveUpdate = (event) => setSaved(event.detail || DEFAULT_PREVIEW_DISPLAY);
    window.addEventListener(PREVIEW_DISPLAY_EVENT, receiveUpdate);
    if (typeof getEmailPreviewDisplayAPI !== "function") return () => window.removeEventListener(PREVIEW_DISPLAY_EVENT, receiveUpdate);
    if (!previewDisplayRequest) previewDisplayRequest = getEmailPreviewDisplayAPI(token).catch(() => ({ previewDisplay: DEFAULT_PREVIEW_DISPLAY }));
    previewDisplayRequest.then((result) => setSaved(result.previewDisplay || DEFAULT_PREVIEW_DISPLAY));
    return () => window.removeEventListener(PREVIEW_DISPLAY_EVENT, receiveUpdate);
  }, [candidateSettings, token]);
  return useMemo(() => candidateSettings || saved, [candidateSettings, saved]);
};
